import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Popover,
  TextField,
  Button,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MentorAdminTable from "./MentorAdminTable";

const MENTOR_AVAILABILITY_FILTER_KEY = "MentorAvailabilityDateFilter";

function MentorAvailability() {
  const savedFilter = (() => {
    try {
      return JSON.parse(localStorage.getItem(MENTOR_AVAILABILITY_FILTER_KEY)) || {};
    } catch {
      return {};
    }
  })();

  const [activeFilter, setActiveFilter] = useState(savedFilter.activeFilter || "1M");
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(savedFilter.startDate || "");
  const [endDate, setEndDate] = useState(savedFilter.endDate || "");

  useEffect(() => {
    localStorage.setItem(
      MENTOR_AVAILABILITY_FILTER_KEY,
      JSON.stringify({ activeFilter, startDate, endDate }),
    );
  }, [activeFilter, startDate, endDate]);

  const getDateRange = (filter, customStart, customEnd) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split("T")[0];
    if (filter === "custom") return { start: customStart, end: customEnd };
    if (filter === "TODAY") return { start: fmt(today), end: fmt(today) };
    if (filter === "YESTERDAY") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: fmt(yesterday), end: fmt(yesterday) };
    }
    const end = fmt(today);
    const start = new Date(today);
    if (filter === "1W") start.setDate(start.getDate() - 7);
    else if (filter === "1M") start.setMonth(start.getMonth() - 1);
    else if (filter === "3M") start.setMonth(start.getMonth() - 3);
    else if (filter === "6M") start.setMonth(start.getMonth() - 6);
    else start.setFullYear(start.getFullYear() - 1);
    return { start: fmt(start), end };
  };

  const { start: resolvedStart, end: resolvedEnd } = getDateRange(
    activeFilter,
    startDate,
    endDate,
  );

  const handleFilterChange = (val) => {
    setActiveFilter(val);
    setAnchorEl(null);
  };

  const handleCustomApply = () => {
    if (startDate && endDate) {
      setActiveFilter("custom");
      setAnchorEl(null);
    }
  };

  const filterLabel =
    activeFilter === "custom" && startDate && endDate
      ? `${startDate} – ${endDate}`
      : activeFilter === "TODAY"
        ? "Today"
        : activeFilter === "YESTERDAY"
          ? "Yesterday"
          : activeFilter === "1W"
            ? "Last Week"
            : activeFilter === "1M"
              ? "Last 1 Month"
              : activeFilter === "3M"
                ? "Last 3 Months"
                : activeFilter === "6M"
                  ? "Last 6 Months"
                  : "Last 1 Year";

  return (
    <Box>
      {/* Filter Bar */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2.5 }}>
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            cursor: "pointer",
            background: "rgba(255,255,255,0.85)",
            border: "1px solid #e8eaf0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            backdropFilter: "blur(8px)",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              borderColor: "#bdbdbd",
            },
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: 16, color: "#667eea" }} />
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#444" }}>
            {filterLabel}
          </Typography>
          <Box sx={{ fontSize: 10, color: "#aaa", ml: 0.5 }}>▼</Box>
        </Box>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              boxShadow: "0 12px 40px rgba(0,0,0,0.13)",
              minWidth: 220,
              overflow: "hidden",
              border: "1px solid #f0f0f0",
            },
          }}
        >
          {[
            { label: "Today", value: "TODAY" },
            { label: "Yesterday", value: "YESTERDAY" },
            { label: "Last Week", value: "1W" },
            { label: "Last 1 Month", value: "1M" },
            { label: "Last 3 Months", value: "3M" },
            { label: "Last 6 Months", value: "6M" },
            { label: "Last 1 Year", value: "1Y" },
          ].map((opt) => (
            <Box
              key={opt.value}
              onClick={() => handleFilterChange(opt.value)}
              sx={{
                px: 2.5,
                py: 1.2,
                cursor: "pointer",
                fontSize: "0.83rem",
                fontWeight: activeFilter === opt.value ? 700 : 500,
                color: activeFilter === opt.value ? "#667eea" : "#444",
                background: activeFilter === opt.value ? "rgba(102,126,234,0.07)" : "#fff",
                borderLeft: activeFilter === opt.value ? "3px solid #667eea" : "3px solid transparent",
                transition: "all 0.15s",
                "&:hover": { background: "rgba(102,126,234,0.06)", color: "#667eea" },
              }}
            >
              {opt.label}
            </Box>
          ))}

          <Box sx={{ height: "1px", background: "#f0f0f0", mx: 2 }} />

          <Box sx={{ p: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="#aaa"
              sx={{ textTransform: "uppercase", letterSpacing: 1, fontSize: "0.68rem" }}
            >
              Custom Range
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mt: 1 }}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.82rem" } }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate }}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.82rem" } }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setActiveFilter("1M");
                    setAnchorEl(null);
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    borderColor: "#e0e0e0",
                    color: "#888",
                  }}
                >
                  Clear
                </Button>
                <Button
                  size="small"
                  fullWidth
                  variant="contained"
                  onClick={handleCustomApply}
                  disabled={!startDate || !endDate}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    boxShadow: "none",
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>

      <MentorAdminTable startDate={resolvedStart} endDate={resolvedEnd} />
    </Box>
  );
}

export default MentorAvailability;
